/**
 * Home-language (EAL) support – the single source of truth for which
 * languages a resource can be built in, and for the fixed strings that get a
 * translated line beneath them.
 *
 * Two kinds of text end up on a bilingual sheet, and they are handled
 * differently on purpose:
 *
 *   1. The FIXED furniture – section instructions, "Key words", "Name",
 *      "Date". There are only ever seven instructions and a handful of
 *      labels, so they are translated here, once, by hand. Asking a model to
 *      re-translate "Circle the letter of the correct answer." on every
 *      generation costs tokens, varies between sheets, and can silently come
 *      back wrong in a script nobody on the team reads.
 *   2. The SUBJECT vocabulary in the glossary, which is different on every
 *      sheet and so has to come from the model (see the `translation` field
 *      on GLOSSARY_SCHEMA in utils/worksheetGen.js).
 *
 * Loaded in the browser as window.HomeLanguages (plain <script>) and
 * require()'d in Node by the generators, routes and the PDF renderer – same
 * UMD shape as worksheet-render.js, and no DOM use, so it works in both.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.HomeLanguages = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /**
     * The list a teacher picks from: the home languages most spoken by EAL
     * pupils in UK schools, plus the ones recent arrivals bring.
     *
     * Deliberately a fixed list rather than a free-text box. Every entry here
     * is one we can promise a font for in the PDF container (see the
     * fonts-noto-* packages in the Dockerfile) and whose direction we handle;
     * a typed-in language could be neither, and the failure mode is a page of
     * empty boxes that only shows up after printing.
     *
     * `endonym` is the language's own name for itself, shown beside the
     * English name so a teacher and a parent are picking the same thing.
     * `dir` drives the `dir` attribute on the translated line, so an RTL
     * script sets its own direction inside an otherwise LTR page.
     * `font` names the family the print CSS reaches for first, and must match
     * a family the container actually installs.
     */
    const LANGUAGES = [
        { code: 'ur', name: 'Urdu',        endonym: 'اردو',        dir: 'rtl', font: "'Noto Nastaliq Urdu', 'Noto Sans Arabic'" },
        { code: 'pl', name: 'Polish',      endonym: 'Polski',       dir: 'ltr', font: '' },
        { code: 'pa', name: 'Punjabi',     endonym: 'ਪੰਜਾਬੀ',        dir: 'ltr', font: "'Noto Sans Gurmukhi'" },
        { code: 'ro', name: 'Romanian',    endonym: 'Română',       dir: 'ltr', font: '' },
        { code: 'ar', name: 'Arabic',      endonym: 'العربية',      dir: 'rtl', font: "'Noto Sans Arabic'" },
        { code: 'fa', name: 'Persian',     endonym: 'فارسی',        dir: 'rtl', font: "'Noto Sans Arabic'" },
        { code: 'bn', name: 'Bengali',     endonym: 'বাংলা',         dir: 'ltr', font: "'Noto Sans Bengali'" },
        { code: 'so', name: 'Somali',      endonym: 'Soomaali',     dir: 'ltr', font: '' },
        { code: 'pt', name: 'Portuguese',  endonym: 'Português',    dir: 'ltr', font: '' },
        { code: 'es', name: 'Spanish',     endonym: 'Español',      dir: 'ltr', font: '' },
        { code: 'uk', name: 'Ukrainian',   endonym: 'Українська',   dir: 'ltr', font: '' },
        { code: 'tr', name: 'Turkish',     endonym: 'Türkçe',       dir: 'ltr', font: '' },
        { code: 'sq', name: 'Albanian',    endonym: 'Shqip',        dir: 'ltr', font: '' },
        { code: 'ta', name: 'Tamil',       endonym: 'தமிழ்',         dir: 'ltr', font: "'Noto Sans Tamil'" },
        { code: 'zh', name: 'Chinese',     endonym: '中文',          dir: 'ltr', font: "'Noto Sans CJK SC', 'Noto Sans SC'" },
    ];

    const BY_CODE = LANGUAGES.reduce((acc, l) => { acc[l.code] = l; return acc; }, {});

    /**
     * The picked language, or null.
     *
     * Everything downstream goes through this, so an unknown code from an old
     * saved sheet or a hand-rolled API call degrades to a monolingual sheet
     * rather than to a half-translated one.
     */
    function getLanguage(code) {
        return BY_CODE[String(code || '').trim().toLowerCase()] || null;
    }

    /** The code if we know it, '' otherwise – for storing and for requests. */
    function cleanLanguage(code) {
        const lang = getLanguage(code);
        return lang ? lang.code : '';
    }

    /**
     * The translated section instructions.
     *
     * Keys match WS_SECTION_INSTRUCTIONS in worksheet-render.js. `whiteboard`
     * is translated from the PRINTED wording ("in the box"), because on paper
     * there is no whiteboard – the on-screen quiz uses the same line, which is
     * close enough that a second variant is not worth maintaining.
     *
     * Written for the pupil, in the register a school would use with a child,
     * and kept to the same one-instruction-per-sentence shape as the English.
     */
    const SECTION_INSTRUCTIONS = {
        ur: {
            multiple_choice:   'درست جواب کے حرف کے گرد دائرہ لگائیں۔',
            true_false:        'ہر بیان کے لیے درست یا غلط پر دائرہ لگائیں۔',
            fill_blank:        'ہر جملے کو درست لفظ یا فقرے سے مکمل کریں۔',
            short_answer:      'ہر سوال کا جواب مکمل جملوں میں دیں۔',
            matching:          'ملنے والی چیز کا حرف خانے میں لکھیں۔',
            extended_response: 'مکمل اور تفصیلی پیراگراف میں جواب دیں۔ ہر سوال کے نمبر دیے گئے ہیں۔',
            whiteboard:        'اپنا جواب خانے میں لکھیں یا بنائیں، اور اپنا طریقۂ کار دکھائیں۔',
        },
        pl: {
            multiple_choice:   'Zakreśl literę poprawnej odpowiedzi.',
            true_false:        'Zakreśl Prawda lub Fałsz przy każdym zdaniu.',
            fill_blank:        'Uzupełnij każde zdanie właściwym słowem lub wyrażeniem.',
            short_answer:      'Odpowiedz na każde pytanie pełnymi zdaniami.',
            matching:          'Wpisz w ramce literę pasującego elementu.',
            extended_response: 'Odpowiedz pełnymi, rozwiniętymi akapitami. Przy każdym pytaniu podano punkty.',
            whiteboard:        'Napisz lub narysuj odpowiedź w ramce, pokazując swoje obliczenia.',
        },
        pa: {
            multiple_choice:   'ਸਹੀ ਜਵਾਬ ਦੇ ਅੱਖਰ ਦੁਆਲੇ ਗੋਲਾ ਲਾਓ।',
            true_false:        'ਹਰ ਵਾਕ ਲਈ ਸਹੀ ਜਾਂ ਗਲਤ ਉੱਤੇ ਗੋਲਾ ਲਾਓ।',
            fill_blank:        'ਹਰ ਵਾਕ ਨੂੰ ਸਹੀ ਸ਼ਬਦ ਜਾਂ ਵਾਕਾਂਸ਼ ਨਾਲ ਪੂਰਾ ਕਰੋ।',
            short_answer:      'ਹਰ ਸਵਾਲ ਦਾ ਜਵਾਬ ਪੂਰੇ ਵਾਕਾਂ ਵਿੱਚ ਦਿਓ।',
            matching:          'ਮਿਲਦੀ ਚੀਜ਼ ਦਾ ਅੱਖਰ ਡੱਬੇ ਵਿੱਚ ਲਿਖੋ।',
            extended_response: 'ਪੂਰੇ ਅਤੇ ਵਿਸਥਾਰ ਵਾਲੇ ਪੈਰਿਆਂ ਵਿੱਚ ਜਵਾਬ ਦਿਓ। ਹਰ ਸਵਾਲ ਦੇ ਅੰਕ ਦਿੱਤੇ ਗਏ ਹਨ।',
            whiteboard:        'ਆਪਣਾ ਜਵਾਬ ਡੱਬੇ ਵਿੱਚ ਲਿਖੋ ਜਾਂ ਬਣਾਓ, ਅਤੇ ਆਪਣਾ ਕੰਮ ਦਿਖਾਓ।',
        },
        ro: {
            multiple_choice:   'Încercuiește litera răspunsului corect.',
            true_false:        'Încercuiește Adevărat sau Fals pentru fiecare afirmație.',
            fill_blank:        'Completează fiecare propoziție cu cuvântul sau expresia corectă.',
            short_answer:      'Răspunde la fiecare întrebare în propoziții complete.',
            matching:          'Scrie în casetă litera elementului care se potrivește.',
            extended_response: 'Răspunde în paragrafe complete și dezvoltate. Punctajul este indicat la fiecare întrebare.',
            whiteboard:        'Scrie sau desenează răspunsul în casetă, arătând rezolvarea.',
        },
        ar: {
            multiple_choice:   'ضع دائرة حول حرف الإجابة الصحيحة.',
            true_false:        'ضع دائرة حول «صح» أو «خطأ» لكل عبارة.',
            fill_blank:        'أكمل كل جملة بالكلمة أو العبارة الصحيحة.',
            short_answer:      'أجب عن كل سؤال بجمل كاملة.',
            matching:          'اكتب حرف العنصر المطابق في المربع.',
            extended_response: 'أجب بفقرات كاملة ومفصّلة. الدرجات موضّحة لكل سؤال.',
            whiteboard:        'اكتب إجابتك أو ارسمها في المربع، مع توضيح خطوات عملك.',
        },
        fa: {
            multiple_choice:   'دور حرف پاسخ درست دایره بکشید.',
            true_false:        'برای هر جمله، دور «درست» یا «نادرست» دایره بکشید.',
            fill_blank:        'هر جمله را با کلمه یا عبارت درست کامل کنید.',
            short_answer:      'به هر پرسش با جمله‌های کامل پاسخ دهید.',
            matching:          'حرف گزینهٔ متناظر را در کادر بنویسید.',
            extended_response: 'با بندهای کامل و گسترده پاسخ دهید. نمرهٔ هر پرسش نشان داده شده است.',
            whiteboard:        'پاسخ خود را در کادر بنویسید یا بکشید و روند کار را نشان دهید.',
        },
        bn: {
            multiple_choice:   'সঠিক উত্তরের অক্ষরটি গোল করে চিহ্নিত করো।',
            true_false:        'প্রতিটি বাক্যের জন্য সত্য বা মিথ্যা গোল করে চিহ্নিত করো।',
            fill_blank:        'প্রতিটি বাক্য সঠিক শব্দ বা বাক্যাংশ দিয়ে পূরণ করো।',
            short_answer:      'প্রতিটি প্রশ্নের উত্তর পূর্ণ বাক্যে দাও।',
            matching:          'মিলে যাওয়া বিষয়টির অক্ষর বাক্সে লেখো।',
            extended_response: 'পূর্ণ ও বিস্তারিত অনুচ্ছেদে উত্তর দাও। প্রতিটি প্রশ্নের নম্বর দেওয়া আছে।',
            whiteboard:        'তোমার উত্তর বাক্সে লেখো বা আঁকো, এবং কাজের ধাপ দেখাও।',
        },
        so: {
            multiple_choice:   'Goobo ku hareeri xarafka jawaabta saxda ah.',
            true_false:        'Weedh kasta u goobo Run ama Been.',
            fill_blank:        'Weedh kasta ku buuxi erayga ama weedha saxda ah.',
            short_answer:      "Su'aal kasta ku jawaab weedho dhammaystiran.",
            matching:          'Ku qor sanduuqa xarafka shayga u dhigma.',
            extended_response: "Ku jawaab baragaraafyo dhammaystiran oo faahfaahsan. Dhibcaha su'aal kasta waa lagu qoray.",
            whiteboard:        'Jawaabtaada ku qor ama ku sawir sanduuqa, adigoo tusaya sida aad u xalisay.',
        },
        pt: {
            multiple_choice:   'Circula a letra da resposta correta.',
            true_false:        'Circula Verdadeiro ou Falso em cada afirmação.',
            fill_blank:        'Completa cada frase com a palavra ou expressão correta.',
            short_answer:      'Responde a cada pergunta em frases completas.',
            matching:          'Escreve na caixa a letra do item correspondente.',
            extended_response: 'Responde em parágrafos completos e desenvolvidos. A cotação está indicada em cada pergunta.',
            whiteboard:        'Escreve ou desenha a tua resposta na caixa, mostrando os teus cálculos.',
        },
        es: {
            multiple_choice:   'Rodea con un círculo la letra de la respuesta correcta.',
            true_false:        'Rodea Verdadero o Falso en cada afirmación.',
            fill_blank:        'Completa cada frase con la palabra o expresión correcta.',
            short_answer:      'Responde a cada pregunta con frases completas.',
            matching:          'Escribe en el recuadro la letra del elemento que corresponde.',
            extended_response: 'Responde en párrafos completos y desarrollados. La puntuación se indica en cada pregunta.',
            whiteboard:        'Escribe o dibuja tu respuesta en el recuadro, mostrando tu procedimiento.',
        },
        uk: {
            multiple_choice:   'Обведи літеру правильної відповіді.',
            true_false:        'Обведи «Правда» або «Неправда» для кожного твердження.',
            fill_blank:        'Доповни кожне речення правильним словом або словосполученням.',
            short_answer:      'Дай відповідь на кожне запитання повними реченнями.',
            matching:          'Запиши в рамці літеру відповідного елемента.',
            extended_response: 'Дай відповідь повними, розгорнутими абзацами. Бали вказано біля кожного запитання.',
            whiteboard:        'Напиши або намалюй відповідь у рамці, показавши свої обчислення.',
        },
        tr: {
            multiple_choice:   'Doğru cevabın harfini daire içine al.',
            true_false:        "Her ifade için Doğru ya da Yanlış'ı daire içine al.",
            fill_blank:        'Her cümleyi doğru kelime veya ifadeyle tamamla.',
            short_answer:      'Her soruyu tam cümlelerle cevapla.',
            matching:          'Eşleşen maddenin harfini kutuya yaz.',
            extended_response: 'Tam ve ayrıntılı paragraflarla cevapla. Her sorunun puanı belirtilmiştir.',
            whiteboard:        'Cevabını kutuya yaz ya da çiz; işlemlerini de göster.',
        },
        sq: {
            multiple_choice:   'Rretho shkronjën e përgjigjes së saktë.',
            true_false:        'Rretho E saktë ose E gabuar për çdo pohim.',
            fill_blank:        'Plotëso çdo fjali me fjalën ose shprehjen e saktë.',
            short_answer:      'Përgjigju çdo pyetjeje me fjali të plota.',
            matching:          'Shkruaj në kuti shkronjën e njësisë përkatëse.',
            extended_response: 'Përgjigju me paragrafë të plotë dhe të zhvilluar. Pikët tregohen te çdo pyetje.',
            whiteboard:        'Shkruaj ose vizato përgjigjen në kuti, duke treguar punën tënde.',
        },
        ta: {
            multiple_choice:   'சரியான விடையின் எழுத்தை வட்டமிடு.',
            true_false:        'ஒவ்வொரு கூற்றுக்கும் சரி அல்லது தவறு என்பதை வட்டமிடு.',
            fill_blank:        'ஒவ்வொரு வாக்கியத்தையும் சரியான சொல் அல்லது சொற்றொடரால் நிரப்பு.',
            short_answer:      'ஒவ்வொரு வினாவுக்கும் முழு வாக்கியங்களில் விடையளி.',
            matching:          'பொருந்தும் உருப்படியின் எழுத்தைப் பெட்டியில் எழுது.',
            extended_response: 'முழுமையான, விரிவான பத்திகளில் விடையளி. ஒவ்வொரு வினாவுக்கும் மதிப்பெண்கள் கொடுக்கப்பட்டுள்ளன.',
            whiteboard:        'உன் விடையைப் பெட்டியில் எழுது அல்லது வரை; செய்முறையையும் காட்டு.',
        },
        zh: {
            multiple_choice:   '圈出正确答案的字母。',
            true_false:        '为每个陈述圈出「正确」或「错误」。',
            fill_blank:        '用正确的词或短语补全每个句子。',
            short_answer:      '用完整的句子回答每个问题。',
            matching:          '在方框中写出对应项目的字母。',
            extended_response: '用完整、展开的段落作答。每题的分数已标出。',
            whiteboard:        '在方框中写出或画出你的答案，并展示解题过程。',
        },
    };

    /**
     * The handful of page labels that get a translated twin.
     *
     * `meaning` is the glossary's home-language column header, which is why it
     * is a label rather than a section instruction – it heads a column, not a
     * task. `contents`, `glossary`, `answers` and the three tier names are the
     * booklet's furniture: a booklet has no per-task instructions to
     * translate, so what an EAL pupil needs from it is to be able to navigate
     * it – to know which page is the answers and which exercises are the warm-up.
     */
    const UI_STRINGS = {
        ur: { keyWords: 'کلیدی الفاظ', meaning: 'معنی',  name: 'نام',        date: 'تاریخ',       instructions: 'ہدایات',
             contents: 'فہرست', glossary: 'لغت', answers: 'جوابات',
             warmup: 'ابتدائی مشق', practice: 'مشق', examStyle: 'امتحانی طرز' },
        pl: { keyWords: 'Kluczowe słowa', meaning: 'Znaczenie', name: 'Imię i nazwisko', date: 'Data', instructions: 'Instrukcje',
             contents: 'Spis treści', glossary: 'Słowniczek', answers: 'Odpowiedzi',
             warmup: 'Rozgrzewka', practice: 'Ćwiczenia', examStyle: 'Zadania egzaminacyjne' },
        pa: { keyWords: 'ਮੁੱਖ ਸ਼ਬਦ',  meaning: 'ਅਰਥ',   name: 'ਨਾਂ',         date: 'ਤਾਰੀਖ਼',      instructions: 'ਹਦਾਇਤਾਂ',
             contents: 'ਤਤਕਰਾ', glossary: 'ਸ਼ਬਦਾਵਲੀ', answers: 'ਜਵਾਬ',
             warmup: 'ਸ਼ੁਰੂਆਤੀ ਅਭਿਆਸ', practice: 'ਅਭਿਆਸ', examStyle: 'ਪ੍ਰੀਖਿਆ-ਸ਼ੈਲੀ' },
        ro: { keyWords: 'Cuvinte-cheie', meaning: 'Sens', name: 'Nume',      date: 'Data',        instructions: 'Instrucțiuni',
             contents: 'Cuprins', glossary: 'Glosar', answers: 'Răspunsuri',
             warmup: 'Încălzire', practice: 'Exerciții', examStyle: 'Tip examen' },
        ar: { keyWords: 'الكلمات المفتاحية', meaning: 'المعنى', name: 'الاسم', date: 'التاريخ',   instructions: 'التعليمات',
             contents: 'المحتويات', glossary: 'المسرد', answers: 'الإجابات',
             warmup: 'تمهيد', practice: 'تدريب', examStyle: 'على نمط الامتحان' },
        fa: { keyWords: 'واژگان کلیدی', meaning: 'معنی', name: 'نام',        date: 'تاریخ',       instructions: 'راهنما',
             contents: 'فهرست', glossary: 'واژه\u200cنامه', answers: 'پاسخ\u200cها',
             warmup: 'تمرین آمادگی', practice: 'تمرین', examStyle: 'سبک امتحان' },
        bn: { keyWords: 'মূল শব্দ',    meaning: 'অর্থ',  name: 'নাম',        date: 'তারিখ',      instructions: 'নির্দেশাবলি',
             contents: 'সূচিপত্র', glossary: 'শব্দকোষ', answers: 'উত্তর',
             warmup: 'প্রস্তুতি', practice: 'অনুশীলন', examStyle: 'পরীক্ষার ধাঁচে' },
        so: { keyWords: 'Erayada muhiimka ah', meaning: 'Macnaha', name: 'Magaca', date: 'Taariikhda', instructions: 'Tilmaamaha',
             contents: 'Tusmada', glossary: 'Qaamuuska', answers: 'Jawaabaha',
             warmup: 'Kulaylin', practice: 'Tababar', examStyle: 'Qaab imtixaan' },
        pt: { keyWords: 'Palavras-chave', meaning: 'Significado', name: 'Nome', date: 'Data',     instructions: 'Instruções',
             contents: 'Índice', glossary: 'Glossário', answers: 'Respostas',
             warmup: 'Aquecimento', practice: 'Prática', examStyle: 'Tipo exame' },
        es: { keyWords: 'Palabras clave', meaning: 'Significado', name: 'Nombre', date: 'Fecha',  instructions: 'Instrucciones',
             contents: 'Índice', glossary: 'Glosario', answers: 'Respuestas',
             warmup: 'Calentamiento', practice: 'Práctica', examStyle: 'Tipo examen' },
        uk: { keyWords: 'Ключові слова', meaning: 'Значення', name: 'Ім’я',   date: 'Дата',        instructions: 'Інструкції',
             contents: 'Зміст', glossary: 'Словник', answers: 'Відповіді',
             warmup: 'Розминка', practice: 'Практика', examStyle: 'Екзаменаційного типу' },
        tr: { keyWords: 'Anahtar kelimeler', meaning: 'Anlamı', name: 'Ad',   date: 'Tarih',       instructions: 'Yönerge',
             contents: 'İçindekiler', glossary: 'Sözlük', answers: 'Cevaplar',
             warmup: 'Isınma', practice: 'Alıştırma', examStyle: 'Sınav tarzı' },
        sq: { keyWords: 'Fjalë kyçe', meaning: 'Kuptimi', name: 'Emri',       date: 'Data',        instructions: 'Udhëzime',
             contents: 'Përmbajtja', glossary: 'Fjalorth', answers: 'Përgjigjet',
             warmup: 'Ngrohje', practice: 'Ushtrime', examStyle: 'Tip provimi' },
        ta: { keyWords: 'முக்கியச் சொற்கள்', meaning: 'பொருள்', name: 'பெயர்', date: 'தேதி',      instructions: 'வழிமுறைகள்',
             contents: 'பொருளடக்கம்', glossary: 'சொற்களஞ்சியம்', answers: 'விடைகள்',
             warmup: 'தொடக்கப் பயிற்சி', practice: 'பயிற்சி', examStyle: 'தேர்வு பாணி' },
        zh: { keyWords: '关键词',      meaning: '意思',  name: '姓名',        date: '日期',        instructions: '说明',
             contents: '目录', glossary: '词汇表', answers: '答案',
             warmup: '热身', practice: '练习', examStyle: '考试题型' },
    };

    /** The translated section instruction, or '' when we have no line for it. */
    function sectionInstruction(code, type) {
        const lang = getLanguage(code);
        if (!lang) return '';
        return (SECTION_INSTRUCTIONS[lang.code] || {})[type] || '';
    }

    /** One translated page label ('keyWords' | 'meaning' | 'name' | 'date' | 'instructions'). */
    function uiString(code, key) {
        const lang = getLanguage(code);
        if (!lang) return '';
        return (UI_STRINGS[lang.code] || {})[key] || '';
    }

    return {
        LANGUAGES, SECTION_INSTRUCTIONS, UI_STRINGS,
        getLanguage, cleanLanguage, sectionInstruction, uiString,
    };
}));
